// ABOUTME: iOS-style bottom sheet component with drag-to-dismiss and backdrop
// Provides native mobile interaction patterns for modal content

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { hapticPatterns } from '@/lib/haptic';
import { X } from 'lucide-react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  className?: string;
  showCloseButton?: boolean;
  maxHeight?: string;
  minHeight?: string;
  snapPoints?: number[];
  initialSnap?: number;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  title,
  className,
  showCloseButton = true,
  maxHeight = '85vh',
  minHeight,
  snapPoints = [0.5, 0.85],
  initialSnap = 0
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Handle animation timing for slide-up effect
  useEffect(() => {
    if (isOpen) {
      // Start with hidden state, then animate in on next frame
      setShouldAnimate(false);
      const timer = requestAnimationFrame(() => {
        setShouldAnimate(true);
      });
      return () => cancelAnimationFrame(timer);
    } else {
      setShouldAnimate(false);
    }
  }, [isOpen]);

  // Manage body scroll and interaction prevention
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent background scrolling and interaction
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.body.classList.add('bottom-sheet-open');
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.classList.remove('bottom-sheet-open');
    };
  }, [isOpen, onClose]);

  // Touch event handlers for drag-to-dismiss
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!sheetRef.current) return;

    const touch = e.touches[0];
    setStartY(touch.clientY);
    setCurrentY(touch.clientY);
    setIsDragging(true);
    hapticPatterns.light();
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || !sheetRef.current) return;

    const touch = e.touches[0];
    const deltaY = touch.clientY - startY;

    // Only allow dragging down
    if (deltaY > 0) {
      setCurrentY(touch.clientY);
      setTranslateY(deltaY);
    }
  }, [isDragging, startY]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    // If dragged down more than 100px, close the sheet
    if (translateY > 100) {
      hapticPatterns.success();
      onClose();
    } else {
      // Snap back
      setTranslateY(0);
    }
  }, [isDragging, translateY, onClose]);

  // Mouse event handlers (for desktop testing)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!sheetRef.current) return;

    setStartY(e.clientY);
    setCurrentY(e.clientY);
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !sheetRef.current) return;

    const deltaY = e.clientY - startY;

    // Only allow dragging down
    if (deltaY > 0) {
      setCurrentY(e.clientY);
      setTranslateY(deltaY);
    }
  }, [isDragging, startY]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    // If dragged down more than 100px, close the sheet
    if (translateY > 100) {
      onClose();
    } else {
      // Snap back
      setTranslateY(0);
    }
  }, [isDragging, translateY, onClose]);

  // Add mouse event listeners for desktop
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Reset translate when closing
  useEffect(() => {
    if (!isOpen) {
      setTranslateY(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const sheetContent = (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ zIndex: 9999 }}
    >
      {/* Backdrop */}
      <div
        className={cn(
          "absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0"
        )}
        onClick={onClose}
        onTouchStart={(e) => e.preventDefault()}
        onTouchMove={(e) => e.preventDefault()}
        onTouchEnd={(e) => e.preventDefault()}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={cn(
          "relative w-full bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out safe-inset-bottom",
          className
        )}
        style={{
          maxHeight,
          minHeight,
          transform: `translateY(calc(${shouldAnimate ? 0 : 100}% + ${translateY}px))`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
        onTouchStartCapture={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            {title && (
              <h2 className="text-xl font-bold text-purple-900">{title}</h2>
            )}
            {!title && <div />}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors touch-scale"
                aria-label="Close"
              >
                <X className="h-4 w-4 text-gray-600" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[80vh]">
          {children}
        </div>
      </div>
    </div>
  );

  // Render in portal for proper z-index layering
  return createPortal(sheetContent, document.body);
};
